/**
 * Delivery date rules shared by the product date picker and the cart's
 * delivery date. Everything is worked out in UK time, wherever the customer
 * is. Calendar dates are held as UTC midnights so day arithmetic and ISO
 * formatting never shift with the visitor's timezone or daylight saving.
 *
 * Each line's rules look like:
 * {
 *   leadTimeDays: 2,
 *   businessDaysOnly: false,
 *   blackoutDays: ['monday'],                 // recurring weekdays
 *   blackoutDayExceptions: ['2026-12-21'],    // lift a recurring weekday
 *   blackoutDates: ['2026-12-25'],            // always blocked
 *   inclusionDates: ['2026-12-01']            // when set, the only dates allowed
 * }
 */
window.DeliveryDates = window.DeliveryDates || (() => {
  const TIME_ZONE = 'Europe/London';
  const DAY_NUMBERS = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  };

  const toISODate = (date) => date.toISOString().split('T')[0];

  const fromISODate = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };

  /**
   * Today's date and the minutes past midnight, in UK time
   */
  function shopNow() {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(new Date()).map((part) => [part.type, part.value])
    );

    return {
      date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
      minutes: Number(parts.hour) * 60 + Number(parts.minute)
    };
  }

  /**
   * Turns a "HH:MM" cutoff into minutes past midnight, or null when not set
   */
  function parseCutoff(value) {
    if (!value || value.trim() === '') return null;

    const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value.trim());

    if (!match) {
      console.warn(`DeliveryDates: Ignoring invalid cutoff time "${value}", expected HH:MM`);
      return null;
    }

    return Number(match[1]) * 60 + Number(match[2]);
  }

  /**
   * A line's earliest date. An order placed after the cutoff, or on a
   * non-working day, counts as placed on the next working day, and the lead
   * time is counted in working days from there. Sundays never count, and
   * business-day products skip Saturdays too
   */
  function earliestDate(rules, now, cutoffMinutes) {
    const isWorkingDay = (date) => {
      const dayOfWeek = date.getUTCDay();
      return dayOfWeek !== 0 && !(rules.businessDaysOnly && dayOfWeek === 6);
    };

    let date = now.date;
    const pastCutoff = cutoffMinutes !== null && now.minutes >= cutoffMinutes;

    if (pastCutoff || !isWorkingDay(date)) {
      do {
        date = addDays(date, 1);
      } while (!isWorkingDay(date));
    }

    for (let added = 0; added < (rules.leadTimeDays || 0);) {
      date = addDays(date, 1);
      if (isWorkingDay(date)) added++;
    }

    return date;
  }

  /**
   * Whether a line allows a date. Inclusion dates override every blackout
   * rule; otherwise blackout dates always apply and a recurring blackout
   * weekday applies unless the date is one of its exceptions
   */
  function allows(rules, iso, dayOfWeek) {
    if (rules.inclusionDates.length > 0) return rules.inclusionDates.includes(iso);
    if (rules.blackoutDates.includes(iso)) return false;

    return !(rules.blackoutDayNumbers.includes(dayOfWeek) && !rules.blackoutDayExceptions.includes(iso));
  }

  const normalise = (rules) => ({
    leadTimeDays: parseInt(rules.leadTimeDays, 10) || 0,
    businessDaysOnly: rules.businessDaysOnly === true || rules.businessDaysOnly === 'true',
    blackoutDayNumbers: (rules.blackoutDays || [])
      .map((name) => DAY_NUMBERS[String(name).trim().toLowerCase()])
      .filter((number) => number !== undefined),
    blackoutDayExceptions: rules.blackoutDayExceptions || [],
    blackoutDates: rules.blackoutDates || [],
    inclusionDates: rules.inclusionDates || []
  });

  /**
   * Every date that suits all of the given lines, from the latest of their
   * earliest dates to a year out (or the furthest inclusion date).
   * Returns { dates, earliest, latest } as YYYY-MM-DD strings
   */
  function availableDates(lines, cutoffTime) {
    const now = shopNow();
    const cutoffMinutes = parseCutoff(cutoffTime);
    const rulesList = lines.map(normalise);

    let start = now.date;
    let end = new Date(Date.UTC(now.date.getUTCFullYear() + 1, now.date.getUTCMonth(), now.date.getUTCDate()));

    rulesList.forEach((rules) => {
      const earliest = earliestDate(rules, now, cutoffMinutes);
      if (earliest > start) start = earliest;

      rules.inclusionDates.forEach((iso) => {
        const date = fromISODate(iso);
        if (date > end) end = date;
      });
    });

    const dates = [];
    for (let date = start; date <= end; date = addDays(date, 1)) {
      const iso = toISODate(date);
      if (rulesList.every((rules) => allows(rules, iso, date.getUTCDay()))) dates.push(iso);
    }

    return { dates, earliest: toISODate(start), latest: toISODate(end) };
  }

  /**
   * "2026-12-01" to "01/12/2026"
   */
  const formatDate = (iso) => iso.split('-').reverse().join('/');

  /**
   * "01/12/2026" to "2026-12-01", or '' when it isn't a DD/MM/YYYY date
   */
  const parseDate = (value) => {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((value || '').trim());
    return match ? `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}` : '';
  };

  return { availableDates, formatDate, parseDate };
})();
